import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { LineMessage, LineThread } from '@/lib/types';
import {
  Banner,
  Field,
  Modal,
  ModalHeader,
  PageHeader,
  Stat,
  Tag,
  inputStyle,
  useToast,
} from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { Select } from '@/components/Select';
import { CustomerPicker } from '@/components/CustomerPicker';
import { SearchInput, matches } from '@/components/SearchInput';

interface BranchLite { id: string; name: string }

export function LinePage() {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const [threadId, setThreadId] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [q, setQ] = useState('');
  const [reply, setReply] = useState('');
  const [incoming, setIncoming] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', customerId: '', tag: '' });

  const threads = useQuery({ queryKey: ['threads'], queryFn: () => api.get<LineThread[]>('/engagement/threads') });
  const branches = useQuery({ queryKey: ['branches'], queryFn: () => api.get<BranchLite[]>('/catalog/branches') });
  const branchName = (id: string | null) => (id ? branches.data?.find((b) => b.id === id)?.name ?? '分店' : '集團共用');
  const messages = useQuery({
    queryKey: ['messages', threadId],
    enabled: !!threadId,
    queryFn: () => api.get<LineMessage[]>(`/engagement/threads/${threadId}/messages`),
  });

  const refreshThreads = () => void qc.invalidateQueries({ queryKey: ['threads'] });
  const refreshMsgs = () => {
    void qc.invalidateQueries({ queryKey: ['messages', threadId] });
    refreshThreads();
  };

  const createThread = useMutation({
    mutationFn: () =>
      api.post<LineThread>('/engagement/threads', {
        name: form.name,
        customerId: form.customerId || undefined,
        tag: form.tag || undefined,
      }),
    onSuccess: (t) => {
      ok('已建立 LINE 對話');
      setOpen(false);
      setForm({ name: '', customerId: '', tag: '' });
      setThreadId(t.id);
      refreshThreads();
    },
    onError: fail,
  });
  const send = useMutation({
    mutationFn: () => api.post('/engagement/threads/send', { threadId, text: reply }),
    onSuccess: () => {
      ok('已送出');
      setReply('');
      refreshMsgs();
    },
    onError: fail,
  });
  const receive = useMutation({
    mutationFn: () => api.post('/engagement/threads/receive', { threadId, text: incoming }),
    onSuccess: () => {
      setIncoming('');
      refreshMsgs();
    },
    onError: fail,
  });
  const read = useMutation({
    mutationFn: (id: string) => api.post(`/engagement/threads/${id}/read`),
    onSuccess: refreshThreads,
    onError: fail,
  });
  const block = useMutation({
    mutationFn: (v: { id: string; blocked: boolean }) =>
      api.post(`/engagement/threads/${v.id}/${v.blocked ? 'unblock' : 'block'}`, { reason: '違規/騷擾' }),
    onSuccess: () => {
      ok('已更新封鎖狀態');
      refreshThreads();
    },
    onError: fail,
  });

  // 綁定顧客（把 LINE 暱稱接上真實顧客資料）
  const [bindOpen, setBindOpen] = useState(false);
  const bind = useMutation({
    mutationFn: (v: { id: string; customerId: string }) => api.post(`/engagement/threads/${v.id}/bind`, { customerId: v.customerId }),
    onSuccess: () => { ok('已綁定顧客'); setBindOpen(false); refreshThreads(); },
    onError: fail,
  });
  const unbind = useMutation({
    mutationFn: (id: string) => api.post(`/engagement/threads/${id}/unbind`),
    onSuccess: () => { ok('已解除綁定'); refreshThreads(); },
    onError: fail,
  });

  const allThreads = threads.data ?? [];
  const list = allThreads.filter(
    (t) =>
      (!q || matches(t.name, q) || matches(t.tag ?? '', q)) &&
      (!branchFilter || (branchFilter === '__group__' ? t.branchId === null : t.branchId === branchFilter)),
  );
  const active = list.find((t) => t.id === threadId);
  const openThread = (id: string) => {
    setThreadId(id);
    read.mutate(id);
  };

  return (
    <div>
      <PageHeader
        title="LINE OA 對話"
        subtitle="官方帳號客服對話 · 封鎖騷擾 · 顧客綁定"
        right={
          <button className="btn btn-primary" onClick={() => setOpen(true)}>
            <Icon d={I.plus} size={13} /> 新對話
          </button>
        }
      />
      <Banner msg={msg} />

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="對話總數" value={String(list.length)} />
        <Stat label="未讀" value={String(list.reduce((s, t) => s + t.unread, 0))} tone="warn" />
        <Stat label="已封鎖" value={String(list.filter((t) => t.blocked).length)} tone="danger" />
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'grid', gridTemplateColumns: '280px 1fr', minHeight: 460 }}>
        <div style={{ borderRight: '1px solid var(--line-soft)', overflow: 'auto' }}>
          <div style={{ padding: 8, borderBottom: '1px solid var(--line-soft)', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <SearchInput value={q} onChange={setQ} placeholder="搜尋對話 / 標籤" width="100%" />
            <Select
              value={branchFilter}
              onChange={setBranchFilter}
              placeholder="全部分店"
              options={[
                { value: '', label: '全部分店' },
                { value: '__group__', label: '集團共用' },
                ...(branches.data ?? []).map((b) => ({ value: b.id, label: b.name })),
              ]}
            />
          </div>
          {list.length === 0 && <div style={{ padding: 16, fontSize: 12, color: 'var(--ink-4)' }}>無相符對話</div>}
          {list.map((t) => (
            <div
              key={t.id}
              onClick={() => openThread(t.id)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid var(--line-soft)',
                background: t.id === threadId ? 'var(--primary-tint)' : 'transparent',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                  <LineAvatar url={t.avatarUrl} name={t.customer?.name ?? t.name} size={26} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 600, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.customer?.name ?? t.name}
                    </span>
                    {t.customer && <span style={{ display: 'block', fontSize: 10, color: 'var(--ink-4)' }}>LINE：{t.name}</span>}
                  </span>
                </span>
                {t.unread > 0 && <Tag tone="danger">{t.unread}</Tag>}
              </div>
              <div style={{ marginTop: 3, display: 'flex', gap: 4, alignItems: 'center' }}>
                <Tag tone={t.branchId ? 'primary' : 'default'}>{branchName(t.branchId)}</Tag>
                {t.customer ? <Tag tone="tag">已綁定</Tag> : <Tag tone="warn">未綁定</Tag>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>
                {t.blocked ? '已封鎖' : t.lastMsg || '（無訊息）'}
              </div>
            </div>
          ))}
          {list.length === 0 && (
            <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 12 }}>尚無對話</div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {active ? (
            <>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--line-soft)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <LineAvatar url={active.avatarUrl} name={active.customer?.name ?? active.name} size={30} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {active.customer?.name ?? active.name}
                    {active.tag && <Tag tone="tag">{active.tag}</Tag>}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--ink-4)' }}>
                    {active.customer
                      ? `LINE：${active.name}${active.customer.phone ? ` · ${active.customer.phone}` : ''}`
                      : 'LINE 暱稱（尚未綁定顧客）'}
                  </div>
                </div>
                <div style={{ flex: 1 }} />
                {active.customer ? (
                  <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => unbind.mutate(active.id)} disabled={unbind.isPending}>
                    解除綁定
                  </button>
                ) : (
                  <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => setBindOpen(true)}>
                    <Icon d={I.user} size={12} /> 綁定顧客
                  </button>
                )}
                <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => block.mutate({ id: active.id, blocked: active.blocked })}>
                  {active.blocked ? '解除封鎖' : '封鎖'}
                </button>
              </div>
              <div style={{ flex: 1, overflow: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(messages.data ?? []).map((m) => (
                  <div key={m.id} style={{ textAlign: m.direction === 'OUT' ? 'right' : 'left' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '7px 11px',
                        borderRadius: 10,
                        maxWidth: '75%',
                        fontSize: 12.5,
                        background: m.direction === 'OUT' ? 'var(--primary)' : 'var(--surface-3)',
                        color: m.direction === 'OUT' ? '#fff' : 'var(--ink)',
                      }}
                    >
                      {m.text}
                    </span>
                  </div>
                ))}
                {messages.data?.length === 0 && (
                  <div style={{ color: 'var(--ink-4)', fontSize: 12, textAlign: 'center', marginTop: 40 }}>尚無訊息</div>
                )}
              </div>
              <div style={{ padding: 12, borderTop: '1px solid var(--line-soft)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    placeholder={active.blocked ? '已封鎖，無法回覆' : '輸入回覆…'}
                    disabled={active.blocked}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && reply && !active.blocked && send.mutate()}
                  />
                  <button className="btn btn-primary" disabled={!reply || active.blocked || send.isPending} onClick={() => send.mutate()}>
                    送出
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    style={{ ...inputStyle, flex: 1, fontSize: 11 }}
                    placeholder="模擬顧客來訊（測試）…"
                    value={incoming}
                    onChange={(e) => setIncoming(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && incoming && receive.mutate()}
                  />
                  <button className="btn btn-ghost" style={{ fontSize: 11 }} disabled={!incoming || receive.isPending} onClick={() => receive.mutate()}>
                    收訊
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-4)', fontSize: 13 }}>
              選擇左側對話
            </div>
          )}
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} width={440}>
        <ModalHeader title="新增 LINE 對話" sub="可綁定既有顧客" onClose={() => setOpen(false)} />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="對話名稱 *">
            <input style={inputStyle} value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="王小姐" />
          </Field>
          <Field label="綁定顧客（選填）">
            <CustomerPicker value={form.customerId} onChange={(id) => setForm((s) => ({ ...s, customerId: id }))} placeholder="搜尋顧客（可不綁定）…" />
          </Field>
          <Field label="標籤（選填）">
            <input style={inputStyle} value={form.tag} onChange={(e) => setForm((s) => ({ ...s, tag: e.target.value }))} placeholder="諮詢中" />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setOpen(false)}>
            取消
          </button>
          <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!form.name || createThread.isPending} onClick={() => createThread.mutate()}>
            建立對話
          </button>
        </div>
      </Modal>

      {bindOpen && active && (
        <BindModal
          lineName={active.name}
          onClose={() => setBindOpen(false)}
          onBind={(customerId) => bind.mutate({ id: active.id, customerId })}
          pending={bind.isPending}
          onFail={fail}
        />
      )}
    </div>
  );
}

/** LINE 大頭貼；無圖則用名字首字圓底。 */
function LineAvatar({ url, name, size }: { url: string | null; name: string; size: number }) {
  if (url) {
    return <img src={url} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  }
  return (
    <span style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: 'var(--primary-soft)', color: 'var(--primary-deep)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 700 }}>
      {name.slice(0, 1)}
    </span>
  );
}

/** 綁定顧客：搜尋既有顧客直接綁，或當場 30 秒新建顧客並綁定。 */
function BindModal({ lineName, onClose, onBind, pending, onFail }: { lineName: string; onClose: () => void; onBind: (customerId: string) => void; pending: boolean; onFail: (e: unknown) => void }) {
  const [picked, setPicked] = useState('');
  const [creating, setCreating] = useState(false);
  const [nf, setNf] = useState({ name: lineName, phone: '' });

  const createAndBind = useMutation({
    mutationFn: () => api.post<{ id: string }>('/customers', { name: nf.name, phone: nf.phone }),
    onSuccess: (c) => onBind(c.id),
    onError: onFail,
  });

  return (
    <Modal open onClose={onClose} width={460}>
      <ModalHeader title="綁定顧客" sub={`把 LINE「${lineName}」接上真實顧客資料`} onClose={onClose} />
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!creating ? (
          <>
            <Field label="搜尋既有顧客">
              <CustomerPicker value={picked} onChange={setPicked} placeholder="輸入姓名 / 電話搜尋…" />
            </Field>
            <button className="btn btn-primary" disabled={!picked || pending} onClick={() => onBind(picked)}>
              <Icon d={I.check} size={13} /> 綁定此顧客
            </button>
            <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--ink-4)' }}>
              找不到？<button type="button" onClick={() => setCreating(true)} style={{ background: 'none', border: 0, color: 'var(--primary)', cursor: 'pointer', fontSize: 12 }}>新建顧客並綁定</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>30 秒建檔：填姓名 + 電話，建立後直接綁定這個 LINE 對話。</div>
            <Field label="姓名 *"><input style={inputStyle} value={nf.name} onChange={(e) => setNf((s) => ({ ...s, name: e.target.value }))} /></Field>
            <Field label="電話 *"><input style={inputStyle} className="num" value={nf.phone} onChange={(e) => setNf((s) => ({ ...s, phone: e.target.value }))} placeholder="0912345678" /></Field>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setCreating(false)}>返回搜尋</button>
              <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!nf.name.trim() || !nf.phone.trim() || createAndBind.isPending || pending} onClick={() => createAndBind.mutate()}>
                <Icon d={I.check} size={13} /> 建立並綁定
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
